import Image from "next/image";
import { useRouter } from "next/router";

import { isNonEmptyArray, useBooleanState } from "@enkor/toss-slash";

import { ChevronRightIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import Avatar from "@domains/common/views/components/Avatar/Avatar";
import { UserDetailReviewFile } from "@models/data-contracts";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import WebViewBridge from "@logics/utils/webViewBridge";
import StarRatingIndicator from "@domains/common/views/components/StarRatingIndicator/StarRatingIndicator";

import * as S from "./ReviewCard.style";

interface ReviewCardProps {
  userId: string;
  id: number;
  review: {
    rating: number;
    content: string;
    fileList: UserDetailReviewFile[];
    createdAt: string;
  };
  reviewer: {
    id: number;
    nickname: string;
    profile: string;
  };
  event: {
    title: string;
  };
}

const ReviewCard = ({
  userId,
  id,
  review,
  reviewer,
  event,
}: ReviewCardProps) => {
  const router = useRouter();

  const [isMouseMoving, setIsMouseMoving, unsetIsMouseMoving] =
    useBooleanState();

  const handleClickCard = () => {
    if (isMouseMoving) return;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToProfileReview(userId, String(id));
    } else {
      // NOTE: review list 페이지에서 # 값의 id를 이용해서 해당하는 card에 focus 될 수 있도록 함.
      router.push(`${NAVIGATION_ROUTE.PROFILE.HREF}/${userId}/review#${id}`);
    }
  };

  return (
    <S.Container
      draggable={false}
      onMouseMove={() => setIsMouseMoving()}
      onMouseDown={() => unsetIsMouseMoving()}
      onMouseUp={handleClickCard}
    >
      <S.ReviewerWrapper>
        <Avatar
          size="xl"
          profile={{ url: reviewer.profile, id: reviewer.id }}
        />
        <S.EventAndReviewerInfo>
          <S.ReviewerNameWrapper>
            <S.ReviewerName name="s">{reviewer.nickname}</S.ReviewerName>
            <S.ReviewedTime name="xs">{review.createdAt}</S.ReviewedTime>
          </S.ReviewerNameWrapper>
          <S.EventTitle name="xs">{event.title}</S.EventTitle>
        </S.EventAndReviewerInfo>
      </S.ReviewerWrapper>
      <S.ReviewContentWrapper>
        <S.ReviewContent name="s" $showMore={isNonEmptyArray(review.fileList)}>
          {review.content}
        </S.ReviewContent>
        {isNonEmptyArray(review.fileList) && (
          <S.ImageList>
            {review.fileList.map(
              ({ url, id }) =>
                url && (
                  <S.ImageWrapper key={`review-${id}`}>
                    <Image
                      width={S.REVIEW_IMAGE_SIZE}
                      height={S.REVIEW_IMAGE_SIZE}
                      src={url || "/assets/detail-test-image.png"}
                      alt={`review-${id}`}
                    />
                  </S.ImageWrapper>
                ),
            )}
          </S.ImageList>
        )}
      </S.ReviewContentWrapper>
      <S.RatingWrapper>
        <StarRatingIndicator rating={review.rating} />
        <ChevronRightIcon strokeColor={grayScale.white} />
      </S.RatingWrapper>
    </S.Container>
  );
};

export default ReviewCard;
